import React, { useEffect, Suspense } from 'react';
import './App.css';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import NotFound from './components/NotFound';
import { getCurrentUser, logout } from './utils/auth';
import StudentsPage from './components/Students/StudentsPage';
import Layout from './components/Layout';
import StudentProfile from './components/Students/StudentProfile';
import AddStudentPage from './components/Students/AddStudentPage';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import InterviewsPage from './components/Interview/InterviewsPage';
import ScheduleInterview from './components/Interview/ScheduleInterview';
import EnquiriesList from './components/Enquiry/EnquiriesList';
import EnquiryFormsList from './components/EnquiryForms/EnquiryFormsList';
import FormBuilder from './components/EnquiryForms/FormBuilder';
import CoursesPage from './components/Courses/CoursesPage';
import CourseProfile from './components/Courses/CourseProfile';
import BatchesPage from './components/Batches/BatchesPage';
import BatchProfile from './components/Batches/BatchProfile';
import EditStudentPage from './components/Students/EditStudentPage';
import ImportStudentsPage from './components/Students/ImportStudentsPage';
import AddOrEditCoursePage from './components/Courses/AddOrEditCoursePage';
import AddOrEditBatchPage from './components/Batches/AddOrEditBatchPage';
import EnquiryForm from './components/Enquiry/EnquiryForm';
import EditEnquiryPage from './components/Enquiry/EditEnquiryPage';
import EnquiryViewPage from './components/Enquiry/EnquiryViewPage';
import FeeCollectionForm from './components/Fees/FeeCollectionForm';
import FeesCollectionPage from './components/Fees/FeesCollectionPage';
import FeeDetailsPage from './components/Fees/FeeDetailsPage';
import EarningsPage from './components/Earnings/EarningsPage';
import AccountSettingsPage from './components/Account/AccountSettingsPage';
import SettingsPage from './components/Settings/SettingsPage';
import Sandbox from './components/Sandbox/Sandbox';
import Signup from './components/Auth/Signup';
import InstructorsPage from './components/Instructors/InstructorsPage';
import AddInstructorPage from './components/Instructors/AddInstructorPage';
import EditInstructorPage from './components/Instructors/EditInstructorPage';
import EnrollPage from './pages/EnrollPage';
import AddEditInterviewPage from './components/Interview/AddEditInterviewPage';
import InterviewDetailsPage from './components/Interview/InterviewDetailsPage';
import StudentInterviewsPage from './components/Interview/StudentInterviewsPage';
import InstructorProfilePage from './components/Instructors/InstructorProfilePage';
import AdminsPage from './components/Admins/AdminsPage';
import EditAdminPage from './components/Admins/EditAdminPage';
import AddAdminPage from './components/Admins/AddAdminPage';
import AdminDetailsPage from './components/Admins/AdminDetailsPage';
import CodingQuestionBankPage from './components/CodingQuestionBank/CodingQuestionBankPage';
import AssignmentCreatePage from './components/Assignments/AssignmentCreatePage';
import AssignmentList from './components/Assignments/AssignmentList';
import AssignmentEditPage from './components/Assignments/AssignmentEditPage';
import StudentAssignmentsPage from './components/Assignments/students/StudentAssignmentsPage';
import StudentAssignmentSessionPage from './components/Assignments/students/StudentAssignmentSessionPage';
import AssignmentDetails from './components/Assignments/AssignmentDetails';
import GeneralAssignmentList from './components/Assignments/general/GeneralAssignmentList';
import GeneralAssignmentForm from './components/Assignments/general/GeneralAssignmentForm';
import GeneralAssignmentDetail from './components/Assignments/general/GeneralAssignmentDetail';
import SubmissionReview from './components/Assignments/general/SubmissionReview';
import StudentAssignmentView from './components/Assignments/general/StudentAssignmentView';
import AddEditAIQuestionPage from './components/AI/AddEditAIQuestionPage';
import AIQuestionsList from './components/AI/AIQuestionsList';
import LearnWithAIPage from './components/AI/LearnWithAIPage';
import StudentAttemptHistory from './components/AI/StudentAttemptHistory';
import AdminAIAttemptHistory from './components/AI/AdminAIAttemptHistory';
import ForgotPassword from './components/Auth/ForgotPassword';
import TermsAndConditions from './components/Legal/TermsAndConditions';
import RecordingsListPage from './components/BatchRecordings/RecordingsListPage';
import RecordingPlayerPage from './components/BatchRecordings/RecordingPlayerPage';
import MyPlanPage from './components/Billing/MyPlanPage';
import InstituteInvoicesPage from './components/Billing/InstituteInvoicesPage';
import InstituteInvoiceDetail from './components/Billing/InstituteInvoiceDetail';
import StoragePage from './components/Storage/StoragePage';
import TrashPage from './components/Trash/TrashPage';
import MockInterviewSetup from './components/MockInterview/MockInterviewSetup';
import MockInterviewSession from './components/MockInterview/MockInterviewSession';
import MockInterviewSummary from './components/MockInterview/MockInterviewSummary';
import MockInterviewHistory from './components/MockInterview/MockInterviewHistory';
import CourseSyllabus from './components/Courses/CourseSyllabus';
import InstructorBatchDetail from './components/Instructors/InstructorBatchDetail';
import InstituteCalendarPage from './components/InstituteCalendar/InstituteCalendarPage';
import StudentSyllabus from './components/Students/Syllabus/StudentSyllabus';
import PracticeQuestionsPage from './components/Practice/PracticeQuestionsPage';
import PracticeCodingPage from './components/Practice/PracticeCodingPage';
import PracticeHistoryPage from './components/Practice/PracticeHistoryPage';
import AdminCodingHistory from './components/Practice/AdminCodingHistory';

// ── Branches ──
import BranchesPage from './components/Branches/BranchesPage';
import AddOrEditBranchPage from './components/Branches/AddOrEditBranchPage';

// ── Self-Paced / LXP ──
import SelfPacedCoursesPage from './components/SelfPaced/SelfPacedCoursesPage';
import AddOrEditSelfPacedCoursePage from './components/SelfPaced/AddOrEditSelfPacedCoursePage';
import SelfPacedCourseProfile from './components/SelfPaced/SelfPacedCourseProfile';
import CourseBuilderPage from './components/SelfPaced/CourseBuilderPage';
import StudentCatalogPage from './components/SelfPaced/StudentCatalogPage';
import StudentCourseDetailPage from './components/SelfPaced/StudentCourseDetailPage';
import StudentLearningPage from './components/SelfPaced/StudentLearningPage';
import StudentEnrollmentsPage from './components/SelfPaced/StudentEnrollmentsPage';
import StudentCertificatesPage from './components/SelfPaced/StudentCertificatesPage';
import CertificateVerifyPage from './components/SelfPaced/CertificateVerifyPage';
import CertificateSettingsPage from './components/SelfPaced/CertificateSettingsPage';

// ── Enrollment (batch-course self-enrollment) ──
import StudentCourseCatalog from './components/Enrollment/StudentCourseCatalog';
import StudentCourseDetail from './components/Enrollment/StudentCourseDetail';
import StudentMyRequests from './components/Enrollment/StudentMyRequests';
import EnrollmentRequestsPage from './components/Enrollment/EnrollmentRequestsPage';

// ── Quiz & Assessment ──
import QuizCategoriesPage from './components/QuizBank/QuizCategoriesPage';
import QuizQuestionsPage from './components/QuizBank/QuizQuestionsPage';
import AddEditQuizQuestionPage from './components/QuizBank/AddEditQuizQuestionPage';
import BulkUploadPage from './components/QuizBank/BulkUploadPage';
import AssessmentsPage from './components/Assessments/AssessmentsPage';
import CreateAssessmentPage from './components/Assessments/CreateAssessmentPage';
import AssessmentDetailsPage from './components/Assessments/AssessmentDetailsPage';
import AnalyticsDashboardPage from './components/Assessments/AnalyticsDashboardPage';
import BulkOperationsPage from './components/Assessments/BulkOperationsPage';

// ── Student Assessments ──
import StudentAssessmentsPage from './components/StudentAssessments/StudentAssessmentsPage';
import AssessmentInfoPage from './components/StudentAssessments/AssessmentInfoPage';
import TakeAssessmentPage from './components/StudentAssessments/TakeAssessmentPage';
import AssessmentResultPage from './components/StudentAssessments/AssessmentResultPage';

// ── SuperAdmin (Tenant Dashboard) ──
import SADashboardPage from './components/SuperAdmin/Dashboard/SADashboardPage';
import InstitutesListPage from './components/SuperAdmin/Institutes/InstitutesListPage';
import InstituteDetailPage from './components/SuperAdmin/Institutes/InstituteDetailPage';
import PlansPage from './components/SuperAdmin/Plans/PlansPage';
import UsersPage from './components/SuperAdmin/Users/UsersPage';
import AuditLogsPage from './components/SuperAdmin/AuditLogs/AuditLogsPage';
import SASettingsPage from './components/SuperAdmin/Settings/SettingsPage';
import SAWhatsAppConfigPage from './components/SuperAdmin/WhatsAppConfig/WhatsAppConfigPage';
import SAZohoConfigPage from './components/SuperAdmin/ZohoConfig/ZohoConfigPage';
import SAZohoMeetingConfigPage from './components/SuperAdmin/ZohoMeetingConfig/ZohoMeetingConfigPage';
import PlatformWhatsAppInbox from './components/SuperAdmin/WhatsAppInbox/PlatformWhatsAppInbox';
import ZoomHostsPage from './components/SuperAdmin/ZoomHosts/ZoomHostsPage';
import PayoutsPage from './components/SuperAdmin/Payouts/PayoutsPage';
import SettlementHistoryPage from './components/SuperAdmin/Payouts/SettlementHistoryPage';
import SAInstitutesOverview from './components/SuperAdmin/Analytics/SAInstitutesOverview';
import SAInstituteAnalytics from './components/SuperAdmin/Analytics/SAInstituteAnalytics';
import FormTemplatesPage from './components/SuperAdmin/FormTemplates/FormTemplatesPage';
import SAStoragePage from './components/SuperAdmin/Storage/SAStoragePage';
import SAAIWhatsAppUsagePage from './components/SuperAdmin/AIWhatsApp/SAAIWhatsAppUsagePage';
import SalesLeadsPage from './components/SuperAdmin/SalesLeads/SalesLeadsPage';
import ServiceInvoicesPage from './components/SuperAdmin/ServiceInvoices/ServiceInvoicesPage';
import ServiceInvoiceForm from './components/SuperAdmin/ServiceInvoices/ServiceInvoiceForm';
import ServiceInvoiceDetail from './components/SuperAdmin/ServiceInvoices/ServiceInvoiceDetail';
import WhatsAppDashboard from './components/WhatsApp/WhatsAppDashboard';
import WhatsAppConnectCallback from './components/WhatsApp/WhatsAppConnectCallback';
import TenantAdminLogin from './components/Auth/TenantAdminLogin';
import RegisterInstitute from './components/Auth/RegisterInstitute';
import JoinInstitute from './components/Auth/JoinInstitute';

import PlanLimitToast from './components/Common/PlanLimitToast';
import PlanGate from './components/Common/PlanGate';
import NotificationToast from './components/Notifications/NotificationToast';
import InAppNotification from './components/Notifications/InAppNotification';
import PWAInstallPrompt from './components/Common/PWAInstallPrompt';
import PWAUpdateToast from './components/Common/PWAUpdateToast';
import StudentMaterialsPage from './components/Materials/StudentMaterialsPage';
import StudentLearningHub from './components/Students/StudentLearningHub';

// ── Support / Help ──
import SupportTicketsPage from './components/Support/SupportTicketsPage';
import SupportTicketDetails from './components/Support/SupportTicketDetails';
import SupportDashboard from './components/Support/SupportDashboard';

// ── Chat ──
import BatchChat from './components/Chat/BatchChat';

// ── Feedback ──
import StudentFeedbackPage from './components/Feedback/StudentFeedbackPage';
import AdminFeedbackPage from './components/Feedback/AdminFeedbackPage';

// ── Attendance ──
import AttendanceManagementPage from './components/Attendance/AttendanceManagementPage';
import AttendanceSessionDetail from './components/Attendance/AttendanceSessionDetail';
import AttendanceAnalyticsPage from './components/Attendance/AttendanceAnalyticsPage';
import MarkOfflineAttendancePage from './components/Attendance/MarkOfflineAttendancePage';
import InstructorAttendancePage from './components/Attendance/InstructorAttendancePage';
const PostClassAttendancePage = React.lazy(() => import('./components/Attendance/PostClassAttendancePage'));

// Lazy-load ClassroomPage to prevent Zoom SDK's bundled CSS from polluting the main bundle
const ClassroomPage = React.lazy(() => import('./components/Classroom/ClassroomPage'));
const InstructorClassroomPage = React.lazy(() => import('./components/Classroom/InstructorClassroomPage'));
const MeetingEndPage = React.lazy(() => import('./components/Classroom/MeetingEndPage'));


const App = () => {
  // ── Institute portal detection ───────────────────────────────────────────
  // On institute subdomains (abc.lmscampus.com) OR custom domains (lms.abc.com),
  // hide the register-institute page since users are on their own portal.
  const hostname = window.location.hostname;
  const isLmsCampusDomain = hostname.endsWith('.lmscampus.com');
  const mainPlatformHosts = ['lmscampus.com', 'www.lmscampus.com', 'lms.lmscampus.com', 'app.lmscampus.com', 'api.lmscampus.com'];
  const isInstituteSubdomain =
    // Subdomain on lmscampus.com (abc.lmscampus.com)
    (isLmsCampusDomain && !mainPlatformHosts.includes(hostname)) ||
    // Custom domain (lms.abcinstitute.com) — not localhost, not lmscampus, not visiblecampus
    (!isLmsCampusDomain && hostname !== 'localhost' && !hostname.endsWith('visiblecampus.com') && hostname.includes('.'));

  // ── Cross-subdomain token hydration ──────────────────────────────────────
  // When redirected from another subdomain (e.g. after institute registration),
  // the token and user are passed via URL query params. Hydrate sessionStorage
  // and strip the params from the URL so they don't leak.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('_token');
    const userParam = params.get('_user');
    if (token && userParam) {
      try {
        const userData = JSON.parse(decodeURIComponent(userParam));
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        window.dispatchEvent(new Event('vc-auth-change'));
      } catch {}
      // Strip params from URL
      params.delete('_token');
      params.delete('_user');
      const clean = params.toString();
      const newUrl = window.location.pathname + (clean ? `?${clean}` : '');
      window.history.replaceState({}, '', newUrl);
      // Force reload so the app picks up the new auth state
      window.location.reload();
      return;
    }
  }, []);

  const user = getCurrentUser();

  useEffect(() => {
    // On mount, let Zoom SDK do its thing.
    return () => {
      // On any unmount, forcibly restore scrolling
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  return (
    <BrowserRouter>
      <PlanLimitToast />
      <NotificationToast />
      <InAppNotification />
      <PWAInstallPrompt />
      <PWAUpdateToast />
      <Routes>
        {/* Default root: Redirect based on login status */}
        <Route
          path="/"
          element={
            user?.usertype === "tenantadmin" ? <Navigate to="/sa" replace />
              : user?.usertype === "student" ? <Navigate to="/s/" replace />
                : user?.usertype === "instructor" ? <Navigate to="/i/" replace />
                  : (user?.usertype === "admin" || user?.usertype === "superadmin") ? <Navigate to="/dashboard" replace />
                    : <Navigate to="/login" replace />
          }
        />

        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        {!isInstituteSubdomain && (
          <Route path="/register-institute" element={<RegisterInstitute />} />
        )}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/enroll" element={<EnrollPage />} />
        <Route path="/join" element={<JoinInstitute />} />
        <Route path="/ta/login" element={<TenantAdminLogin />} />
        <Route path="/verify/:certNo" element={<CertificateVerifyPage />} />
        <Route path="/verify" element={<CertificateVerifyPage />} />


        {/* WhatsApp OAuth callback — accessible by both institute admins and tenant admins */}
        <Route element={<ProtectedRoute allowed={["superadmin", "admin", "tenantadmin"]} />}>
          <Route path="/whatsapp/connect/callback" element={<WhatsAppConnectCallback />} />
        </Route>

        {/* Protected Layout (Sidebar + TopNav) */}
        <Route element={<ProtectedRoute allowed={["superadmin", "admin"]} />}>
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<Dashboard user={user} onLogout={handleLogout} />} />
            <Route path="/students" element={<StudentsPage />} />
            <Route path="/students/:id" element={<StudentProfile />} />
            <Route path="/students/add" element={<AddStudentPage />} />
            <Route path="/students/import" element={<ImportStudentsPage />} />
            <Route path="/students/edit/:id" element={<EditStudentPage />} />
            <Route path="/interviews" element={<PlanGate feature="interviews"><InterviewsPage /></PlanGate>} />
            <Route path="/interviews/:id" element={<PlanGate feature="interviews"><InterviewDetailsPage /></PlanGate>} />
            <Route path="/interviews/new" element={<PlanGate feature="interviews"><AddEditInterviewPage /></PlanGate>} />
            <Route path="/interviews/edit/:id" element={<PlanGate feature="interviews"><AddEditInterviewPage /></PlanGate>} />
            <Route path="/enquiries" element={<PlanGate feature="enquiryManagement"><EnquiriesList /></PlanGate>} />
            <Route path="/enquiries/new" element={<PlanGate feature="enquiryManagement"><EnquiryForm /></PlanGate>} />
            <Route path="/enquiries/edit/:id" element={<PlanGate feature="enquiryManagement"><EditEnquiryPage /></PlanGate>} />
            <Route path="/enquiries/:id" element={<PlanGate feature="enquiryManagement"><EnquiryViewPage /></PlanGate>} />
            <Route path="/enquiry-forms" element={<PlanGate feature="enquiryFormBuilder"><EnquiryFormsList /></PlanGate>} />
            <Route path="/enquiry-forms/new" element={<PlanGate feature="enquiryFormBuilder"><FormBuilder /></PlanGate>} />
            <Route path="/enquiry-forms/edit/:id" element={<PlanGate feature="enquiryFormBuilder"><FormBuilder /></PlanGate>} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/add" element={<AddOrEditCoursePage />} />
            <Route path="/courses/edit/:courseId" element={<AddOrEditCoursePage />} />
            <Route path="/courses/:id" element={<CourseProfile />} />
            <Route path="/courses/:id/syllabus" element={<CourseSyllabus />} />
            <Route path="/batches" element={<BatchesPage />} />
            <Route path="/batches/add" element={<AddOrEditBatchPage />} />
            <Route path="/batches/edit/:batchId" element={<AddOrEditBatchPage />} />
            <Route path="/batches/:id" element={<BatchProfile />} />
            <Route path="/students/:id/fees/new" element={<PlanGate feature="feeManagement"><FeeCollectionForm mode="add" /></PlanGate>} />
            <Route path="/students/:id/fees/edit/:feeId" element={<PlanGate feature="feeManagement"><FeeCollectionForm mode="edit" /></PlanGate>} />
            <Route path="/students/:id/fees/view/:feeId" element={<PlanGate feature="feeManagement"><FeeDetailsPage /></PlanGate>} />
            <Route path="/fees/:feeId/details" element={<PlanGate feature="feeManagement"><FeeDetailsPage /></PlanGate>} />
            <Route path="/fees/new" element={<PlanGate feature="feeManagement"><FeeCollectionForm mode="add" /></PlanGate>} />
            <Route path="/mock-interview/history" element={<PlanGate feature="aiMock"><MockInterviewHistory isAdmin={true} /></PlanGate>} />
            <Route path="/mock-interview/summary/:mockInterviewId" element={<PlanGate feature="aiMock"><MockInterviewSummary /></PlanGate>} />
            {/* Admin Routes */}
            <Route path="/fees" element={<PlanGate feature="feeManagement"><FeesCollectionPage /></PlanGate>} />
            <Route path="/earnings" element={<PlanGate feature="feeManagement"><EarningsPage /></PlanGate>} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/sandbox" element={<Sandbox />} />
            <Route path="/instructors" element={<InstructorsPage />} />
            <Route path="/instructors/:id" element={<InstructorProfilePage />} />
            <Route path="/instructors/add" element={<AddInstructorPage />} />
            <Route path="/instructors/edit/:id" element={<EditInstructorPage />} />
            <Route path="/admins" element={<AdminsPage />} />
            <Route path="/admins/add" element={<AddAdminPage />} />
            <Route path="/admins/:id" element={<AdminDetailsPage />} />
            <Route path="/admins/edit/:id" element={<EditAdminPage />} />
            <Route path="/questions-management" element={<PlanGate feature="aiQuestionBank"><CodingQuestionBankPage /></PlanGate>} />
            <Route path="/coding-submissions" element={<PlanGate feature="codingPractice"><AdminCodingHistory /></PlanGate>} />
            <Route path="/assignments" element={<PlanGate feature="assignments"><AssignmentList /></PlanGate>} />
            <Route path="/create-assignment" element={<PlanGate feature="assignments"><AssignmentCreatePage /></PlanGate>} />
            <Route path="/assignments/edit/:id" element={<PlanGate feature="assignments"><AssignmentEditPage /></PlanGate>} />
            <Route path="/assignments/:id" element={<PlanGate feature="assignments"><AssignmentDetails /></PlanGate>} />
            <Route path="/batch-assignments" element={<GeneralAssignmentList />} />
            <Route path="/batch-assignments/create" element={<GeneralAssignmentForm />} />
            <Route path="/batch-assignments/edit/:id" element={<GeneralAssignmentForm />} />
            <Route path="/batch-assignments/:id" element={<GeneralAssignmentDetail />} />
            <Route path="/batch-assignments/:id/review/:submissionId" element={<SubmissionReview />} />
            <Route path="/ai-questions" element={<PlanGate feature="aiQuestionBank"><AIQuestionsList /></PlanGate>} />
            <Route path="/ai-questions/new" element={<PlanGate feature="aiQuestionBank"><AddEditAIQuestionPage /></PlanGate>} />
            <Route path="/ai-questions/edit/:id" element={<PlanGate feature="aiQuestionBank"><AddEditAIQuestionPage /></PlanGate>} />
            <Route path="/ai-attempts" element={<PlanGate feature="aiLearning"><AdminAIAttemptHistory /></PlanGate>} />

            {/* Quiz & Assessment */}
            <Route path="/quiz-bank/categories" element={<PlanGate feature="quizAssessment"><QuizCategoriesPage /></PlanGate>} />
            <Route path="/quiz-bank" element={<PlanGate feature="quizAssessment"><QuizQuestionsPage /></PlanGate>} />
            <Route path="/quiz-bank/add" element={<PlanGate feature="quizAssessment"><AddEditQuizQuestionPage /></PlanGate>} />
            <Route path="/quiz-bank/edit/:id" element={<PlanGate feature="quizAssessment"><AddEditQuizQuestionPage /></PlanGate>} />
            <Route path="/quiz-bank/bulk-upload" element={<PlanGate feature="quizAssessment"><BulkUploadPage /></PlanGate>} />
            <Route path="/assessments" element={<PlanGate feature="quizAssessment"><AssessmentsPage /></PlanGate>} />
            <Route path="/assessments/create" element={<PlanGate feature="quizAssessment"><CreateAssessmentPage /></PlanGate>} />
            <Route path="/assessments/edit/:id" element={<PlanGate feature="quizAssessment"><CreateAssessmentPage /></PlanGate>} />
            <Route path="/assessments/:id" element={<PlanGate feature="quizAssessment"><AssessmentDetailsPage /></PlanGate>} />
            <Route path="/assessment-analytics" element={<PlanGate feature="quizAssessment"><AnalyticsDashboardPage /></PlanGate>} />
            <Route path="/assessment-bulk" element={<PlanGate feature="quizAssessment"><BulkOperationsPage /></PlanGate>} />

            <Route path="/institute/calendar" element={<InstituteCalendarPage />} />
            <Route path="/account-settings" element={<AccountSettingsPage />} />
            <Route path="/billing" element={<MyPlanPage />} />
            <Route path="/service-invoices" element={<InstituteInvoicesPage />} />
            <Route path="/service-invoices/:id" element={<InstituteInvoiceDetail />} />
            <Route path="/storage" element={<StoragePage />} />
            <Route path="/trash" element={<TrashPage />} />
            <Route path="/whatsapp" element={<PlanGate feature="whatsapp"><WhatsAppDashboard /></PlanGate>} />

            {/* Self-Paced / LXP Admin Routes */}
            <Route path="/self-paced" element={<PlanGate feature="selfPacedCourses"><SelfPacedCoursesPage /></PlanGate>} />
            <Route path="/self-paced/add" element={<PlanGate feature="selfPacedCourses"><AddOrEditSelfPacedCoursePage /></PlanGate>} />
            <Route path="/self-paced/edit/:courseId" element={<PlanGate feature="selfPacedCourses"><AddOrEditSelfPacedCoursePage /></PlanGate>} />
            <Route path="/self-paced/certificate-settings" element={<PlanGate feature="selfPacedCourses"><CertificateSettingsPage /></PlanGate>} />
            <Route path="/self-paced/:id" element={<PlanGate feature="selfPacedCourses"><CourseBuilderPage /></PlanGate>} />

            {/* Support Tickets */}
            <Route path="/support/tickets" element={<SupportTicketsPage />} />
            <Route path="/support/tickets/:id" element={<SupportTicketDetails />} />

            {/* Batch Chat */}
            <Route path="/chat" element={<BatchChat />} />

            {/* Feedback & Ratings */}
            <Route path="/feedback" element={<AdminFeedbackPage />} />

            {/* Enrollment Requests */}
            <Route path="/enrollment-requests" element={<EnrollmentRequestsPage />} />

            {/* Branches */}
            <Route path="/branches" element={<PlanGate feature="multiBranch"><BranchesPage /></PlanGate>} />
            <Route path="/branches/add" element={<PlanGate feature="multiBranch"><AddOrEditBranchPage /></PlanGate>} />
            <Route path="/branches/edit/:branchId" element={<PlanGate feature="multiBranch"><AddOrEditBranchPage /></PlanGate>} />

            {/* Attendance Management */}
            <Route path="/attendance" element={<AttendanceManagementPage />} />
            <Route path="/attendance/offline" element={<MarkOfflineAttendancePage />} />
            <Route path="/attendance/session/:sessionId" element={<AttendanceSessionDetail />} />
            <Route path="/attendance/analytics" element={<AttendanceAnalyticsPage />} />

          </Route>
        </Route>
        <Route element={<ProtectedRoute allowed={["student"]} />}>
          <Route element={<Layout />}>
            <Route path="/s/" element={<StudentProfile />} />
            {/* <Route path="/s/students" element={<StudentProfile />} /> */}
            <Route path="/s/assignments" element={<PlanGate feature="assignments"><StudentAssignmentsPage /></PlanGate>} />
            <Route path="/s/assignment-session/:id" element={<PlanGate feature="assignments"><StudentAssignmentSessionPage /></PlanGate>} />
            <Route path="/s/sandbox" element={<Sandbox />} />
            <Route path="/s/classroom/:batchId" element={<Suspense fallback={<div className="text-center my-5"><div className="spinner-border" /></div>}><ClassroomPage /></Suspense>} />
            <Route path="/s/meeting-ended/:batchId" element={<Suspense fallback={<div className="text-center my-5"><div className="spinner-border" /></div>}><MeetingEndPage /></Suspense>} />
            <Route path="/s/account-settings" element={<AccountSettingsPage />} />
            <Route path="/s/learn-with-ai" element={<PlanGate feature="aiLearning"><LearnWithAIPage /></PlanGate>} />
            <Route path="/s/learn-ai/history" element={<PlanGate feature="aiLearning"><StudentAttemptHistory /></PlanGate>} />
            <Route path="/s/learning" element={<StudentLearningHub />} />
            <Route path="/s/batch-assignment/:id" element={<StudentAssignmentView />} />
            <Route path="/s/class-recordings" element={<RecordingsListPage />} />
            <Route path="/s/recordings/:id" element={<RecordingPlayerPage />} />
            <Route path="/s/materials" element={<StudentMaterialsPage />} />
            <Route path="/s/mock-interview/setup" element={<PlanGate feature="aiMock"><MockInterviewSetup /></PlanGate>} />
            <Route path="/s/mock-interview/session/:mockInterviewId" element={<PlanGate feature="aiMock"><MockInterviewSession /></PlanGate>} />
            <Route path="/s/mock-interview/summary/:mockInterviewId" element={<PlanGate feature="aiMock"><MockInterviewSummary /></PlanGate>} />
            <Route path="/s/mock-interview/history" element={<PlanGate feature="aiMock"><MockInterviewHistory /></PlanGate>} />
            <Route path="/s/syllabus" element={<StudentSyllabus />} />
            <Route path="/s/batch/:batchId/syllabus" element={<StudentSyllabus />} />
            <Route path="/s/practice" element={<PlanGate feature="codingPractice"><PracticeQuestionsPage /></PlanGate>} />
            <Route path="/s/practice/history" element={<PlanGate feature="codingPractice"><PracticeHistoryPage /></PlanGate>} />
            <Route path="/s/practice/:questionId" element={<PlanGate feature="codingPractice"><PracticeCodingPage /></PlanGate>} />
            <Route path="/s/calendar" element={<InstituteCalendarPage />} />
            <Route path="/s/interviews" element={<PlanGate feature="interviews"><StudentInterviewsPage /></PlanGate>} />
            <Route path="/s/interviews/new" element={<PlanGate feature="interviews"><AddEditInterviewPage /></PlanGate>} />
            <Route path="/s/interviews/edit/:id" element={<PlanGate feature="interviews"><AddEditInterviewPage /></PlanGate>} />
            <Route path="/s/interviews/:id" element={<PlanGate feature="interviews"><InterviewDetailsPage /></PlanGate>} />

            {/* Student Assessments / Quizzes */}
            <Route path="/s/assessments" element={<PlanGate feature="quizAssessment"><StudentAssessmentsPage /></PlanGate>} />
            <Route path="/s/assessment/:id" element={<PlanGate feature="quizAssessment"><AssessmentInfoPage /></PlanGate>} />
            <Route path="/s/assessment/take/:attemptId" element={<PlanGate feature="quizAssessment"><TakeAssessmentPage /></PlanGate>} />
            <Route path="/s/assessment/result/:attemptId" element={<PlanGate feature="quizAssessment"><AssessmentResultPage /></PlanGate>} />

            {/* Self-Paced / LXP Student Routes */}
            <Route path="/s/self-paced" element={<PlanGate feature="selfPacedCourses"><StudentCatalogPage /></PlanGate>} />
            <Route path="/s/self-paced/enrollments" element={<PlanGate feature="selfPacedCourses"><StudentEnrollmentsPage /></PlanGate>} />
            <Route path="/s/self-paced/certificates" element={<PlanGate feature="selfPacedCourses"><StudentCertificatesPage /></PlanGate>} />
            <Route path="/s/self-paced/:id" element={<PlanGate feature="selfPacedCourses"><StudentCourseDetailPage /></PlanGate>} />
            <Route path="/s/self-paced/:id/learn" element={<PlanGate feature="selfPacedCourses"><StudentLearningPage /></PlanGate>} />

            {/* Support */}
            <Route path="/s/support" element={<SupportTicketsPage myTicketsOnly basePath="/s/support" />} />
            <Route path="/s/support/:id" element={<SupportTicketDetails backPath="/s/support" />} />

            {/* Feedback */}
            <Route path="/s/feedback" element={<StudentFeedbackPage />} />

            {/* Batch Chat */}
            <Route path="/s/chat" element={<BatchChat />} />

            {/* Batch Course Self-Enrollment */}
            <Route path="/s/courses" element={<StudentCourseCatalog />} />
            <Route path="/s/courses/my-requests" element={<StudentMyRequests />} />
            <Route path="/s/courses/:id" element={<StudentCourseDetail />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute allowed={["instructor"]} />}>
          <Route element={<Layout />}>
            <Route path="/i/" element={<InstructorProfilePage />} />
            <Route path="/i/batch/:batchId" element={<InstructorBatchDetail />} />
            <Route path="/i/classroom/:batchId" element={<Suspense fallback={<div className="text-center my-5"><div className="spinner-border" /></div>}><InstructorClassroomPage /></Suspense>} />
            <Route path="/i/meeting-ended/:batchId" element={<Suspense fallback={<div className="text-center my-5"><div className="spinner-border" /></div>}><MeetingEndPage /></Suspense>} />
            <Route path="/i/post-class-attendance/:batchId" element={<Suspense fallback={<div className="text-center my-5"><div className="spinner-border" /></div>}><PostClassAttendancePage /></Suspense>} />

            {/* Instructor Attendance */}
            <Route path="/i/attendance" element={<InstructorAttendancePage />} />
            <Route path="/i/attendance/offline" element={<MarkOfflineAttendancePage basePath="/i/attendance" />} />
            <Route path="/i/attendance/session/:sessionId" element={<AttendanceSessionDetail basePath="/i/attendance" />} />

            <Route path="/i/questions-management" element={<PlanGate feature="aiQuestionBank"><CodingQuestionBankPage /></PlanGate>} />
            <Route path="/i/coding-submissions" element={<PlanGate feature="codingPractice"><AdminCodingHistory /></PlanGate>} />
            <Route path="/i/assignments" element={<PlanGate feature="assignments"><AssignmentList /></PlanGate>} />
            <Route path="/i/create-assignment" element={<PlanGate feature="assignments"><AssignmentCreatePage /></PlanGate>} />
            <Route path="/i/assignments/edit/:id" element={<PlanGate feature="assignments"><AssignmentEditPage /></PlanGate>} />
            <Route path="/i/assignments/:id" element={<PlanGate feature="assignments"><AssignmentDetails /></PlanGate>} />
            <Route path="/i/batch-assignments" element={<GeneralAssignmentList />} />
            <Route path="/i/batch-assignments/create" element={<GeneralAssignmentForm />} />
            <Route path="/i/batch-assignments/edit/:id" element={<GeneralAssignmentForm />} />
            <Route path="/i/batch-assignments/:id" element={<GeneralAssignmentDetail />} />
            <Route path="/i/batch-assignments/:id/review/:submissionId" element={<SubmissionReview />} />
            <Route path="/i/account-settings" element={<AccountSettingsPage />} />
            <Route path="/i/calendar" element={<InstituteCalendarPage />} />

            {/* Support */}
            <Route path="/i/support" element={<SupportTicketsPage myTicketsOnly basePath="/i/support" />} />
            <Route path="/i/support/:id" element={<SupportTicketDetails backPath="/i/support" />} />

            {/* Batch Chat */}
            <Route path="/i/chat" element={<BatchChat />} />
          </Route>
        </Route>

        {/* ── Tenant Admin Dashboard ── */}
        <Route element={<ProtectedRoute allowed={["tenantadmin"]} />}>
          <Route element={<Layout />}>
            <Route path="/sa" element={<SADashboardPage />} />
            <Route path="/sa/institutes" element={<InstitutesListPage />} />
            <Route path="/sa/institutes/:id" element={<InstituteDetailPage />} />
            <Route path="/sa/plans" element={<PlansPage />} />
            <Route path="/sa/zoom-hosts" element={<ZoomHostsPage />} />
            <Route path="/sa/users" element={<UsersPage />} />
            <Route path="/sa/audit-logs" element={<AuditLogsPage />} />
            <Route path="/sa/payouts" element={<PayoutsPage />} />
            <Route path="/sa/payouts/history" element={<SettlementHistoryPage />} />
            <Route path="/sa/support" element={<SupportDashboard />} />
            <Route path="/sa/support/:id" element={<SupportTicketDetails isAdmin />} />
            <Route path="/sa/settings" element={<SASettingsPage />} />
            <Route path="/sa/whatsapp" element={<SAWhatsAppConfigPage />} />
            <Route path="/sa/whatsapp-inbox" element={<PlatformWhatsAppInbox />} />
            <Route path="/sa/zoho" element={<SAZohoConfigPage />} />
            <Route path="/sa/zoho-meeting" element={<SAZohoMeetingConfigPage />} />
            <Route path="/sa/account-settings" element={<AccountSettingsPage />} />
            <Route path="/sa/analytics" element={<SAInstitutesOverview />} />
            <Route path="/sa/analytics/:id" element={<SAInstituteAnalytics />} />
            <Route path="/sa/form-templates" element={<FormTemplatesPage />} />
            <Route path="/sa/coding-questions" element={<CodingQuestionBankPage mode="library" />} />
            <Route path="/sa/storage" element={<SAStoragePage />} />
            <Route path="/sa/ai-whatsapp" element={<SAAIWhatsAppUsagePage />} />
            <Route path="/sa/sales-leads" element={<SalesLeadsPage />} />
            <Route path="/sa/service-invoices" element={<ServiceInvoicesPage />} />
            <Route path="/sa/service-invoices/create" element={<ServiceInvoiceForm />} />
            <Route path="/sa/service-invoices/edit/:id" element={<ServiceInvoiceForm />} />
            <Route path="/sa/service-invoices/:id" element={<ServiceInvoiceDetail />} />
          </Route>
        </Route>

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
